/**
 * "flowsheet" apparat adapteri — texnologik sxemani oddiy lab ishi kabi ishlatish.
 *
 * Lab JSON: unit: "flowsheet", params: { flowsheet: "<id>", variant?: {...} },
 * inputs: { <nodeId>: { <maydon>: qiymat } } — tugun ustiga qo'yiladigan o'zgarishlar (overrides).
 * Boshqaruv elementi `path` = "preheat.outletT" yoki "purgeSplit.fractions.purge";
 * datchik `source` = "outputs.nodes.reactor.conversion.nitrogen", "outputs.streams.product.flow.ammonia".
 *
 * Faqat turg'un rejim (simulation.mode = "steady"): har boshqaruv o'zgarishida sxema qayta yechiladi.
 * Yaqinlashmasa — chiqishlar oxirgi holat, ogohlantirish `flowsheet.notConverged` (critical).
 */
import type { UnitModel } from '../../types';
import { getFlowsheet, solveFlowsheet, type FlowsheetResult } from '../../flowsheet';

export interface FlowsheetUnitParams {
  flowsheet: string;
  /** Sxema variant parametrlari (lab varianti orqali "$" bilan to'ldiriladi) */
  variant?: Record<string, number | string>;
}
export type FlowsheetUnitInputs = Record<string, Record<string, unknown>>;
export interface FlowsheetUnitState {
  outputs: FlowsheetResult;
}

function solve(params: FlowsheetUnitParams, inputs: FlowsheetUnitInputs): FlowsheetResult {
  if (!params || typeof params.flowsheet !== 'string') throw new Error('flowsheet unit: params.flowsheet (id) required');
  const def = getFlowsheet(params.flowsheet);
  const overrides: Record<string, Record<string, unknown>> = {};
  for (const [node, fields] of Object.entries(inputs ?? {})) {
    if (fields && typeof fields === 'object') overrides[node] = fields;
  }
  return solveFlowsheet(def, { overrides, ...(params.variant ? { variant: params.variant } : {}) });
}

export const flowsheetUnit: UnitModel<FlowsheetUnitParams, FlowsheetUnitInputs, FlowsheetUnitState, FlowsheetResult, FlowsheetResult> = {
  id: 'flowsheet',
  init: (params, inputs) => ({ outputs: solve(params, inputs) }),
  step: (params, _state, inputs) => {
    const outputs = solve(params, inputs);
    return { state: { outputs }, outputs, warnings: outputs.warnings };
  },
  steady: (params, inputs) => {
    const outputs = solve(params, inputs);
    const residual = outputs.blocks.reduce((m, b) => Math.max(m, b.residual), 0);
    return {
      outputs,
      warnings: outputs.warnings,
      diagnostics: {
        converged: outputs.status === 'converged',
        iterations: outputs.iterations,
        residual,
        ...(outputs.status === 'converged' ? {} : { message: outputs.status === 'diverged' ? 'flowsheet.diverged' : 'flowsheet.notConverged' }),
      },
    };
  },
};
